/** Atlas resolution. Powers of two only — the display material mips it. */
export const SCREEN_SIZE = 1024;

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Canvas-space rects, origin top-left, y increasing downward. */
function positions(size: number) {
  const full = size;
  const half = size * 0.5;
  const quarter = size * 0.25;

  return {
    ADSR: { x: 0, y: 0, width: full, height: quarter },
    LOOP: { x: 0, y: quarter, width: half, height: quarter },
    PING_PONG: { x: half, y: quarter, width: half, height: quarter },
    REVERSE: { x: 0, y: half, width: quarter, height: quarter },
    FUZZ: { x: quarter, y: half, width: quarter, height: quarter },
    FILTER: { x: half, y: half, width: quarter, height: quarter },
    ECHO: { x: quarter + half, y: half, width: quarter, height: quarter },
    REVERB: { x: 0, y: quarter + half, width: quarter, height: quarter },
    DEMO_PROGRESS: { x: quarter, y: quarter + half, width: quarter, height: quarter },
    RECORD_PROGRESS: { x: half, y: quarter + half, width: quarter, height: quarter },
    MUSIC_PROGRESS: { x: quarter + half, y: quarter + half, width: quarter, height: quarter },
  } satisfies Record<string, Rect>;
}

export const SCREEN_RECTS = positions(SCREEN_SIZE);

/** Stroke width the original 2D context used, scaled to the atlas size. */
export const LINE_WIDTH = 12 * (SCREEN_SIZE / 1024);
export const MARGIN = 15 * (SCREEN_SIZE / 1024);

export function inset(rect: Rect, margin = MARGIN): Rect {
  return {
    x: rect.x + margin,
    y: rect.y + margin,
    width: rect.width - margin * 2,
    height: rect.height - margin * 2,
  };
}

/**
 * UV sub-rect of the atlas, for meshes that show one region.
 * Returned as `[offsetX, offsetY, repeatX, repeatY]` in texture space, where
 * v runs downward to match the canvas-space layout above.
 */
export function uvRect(rect: Rect): [number, number, number, number] {
  return [
    rect.x / SCREEN_SIZE,
    rect.y / SCREEN_SIZE,
    rect.width / SCREEN_SIZE,
    rect.height / SCREEN_SIZE,
  ];
}
